/* Copyright (c) 2026 Fluxonix Inc. All rights reserved. */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

const INVESTIGATION_REQUEST = /\b(open|start|create|begin|launch|file|log)\b[\s\S]{0,70}\b(incident|investigation|root cause|rca|ncr|nonconformance)\b|\b(investigate|root cause analysis)\b/i;
const HAAS_GUIDE = "https://www.haascnc.com/service/troubleshooting-and-how-to/troubleshooting/servo-bar-300---troubleshooting-guide.html";
const DEMO_ALARMS = [
  { code: "702", title: "V axis position error", terms: ["position error"], checks: ["The trolley may have hit an obstruction. Check the charging tray and path of travel.", "A bar may be stuck. Inspect and clear the bar path using the OEM procedure."] },
  { code: "712", title: "V axis servo overload", terms: ["servo overload"], checks: ["The V axis ballscrew may need inspection for lubrication or damage.", "If the servo motor is the concern, follow the linked Haas motor troubleshooting guidance."] },
  { code: "452", title: "Servo bar motor timeout", terms: ["motor timeout"], checks: ["Check whether the bar feeder lifter is obstructed.", "The source also lists DC motor brushes, the load-bar proximity sensor, and motor voltage as checks."] },
  { code: "423", title: "Servo bar EOB switch position", terms: ["eob switch", "end of bar switch"], checks: ["Check the end-of-bar proximity sensor using the OEM guide."] },
];

function demoAlarmFor(text) {
  const lower = text.toLowerCase();
  if (/\bfuji\b/.test(lower)) return null;
  return DEMO_ALARMS.find((alarm) => new RegExp(`(^|\\D)${alarm.code}(\\D|$)`).test(lower))
    || DEMO_ALARMS.find((alarm) => alarm.terms.some((term) => lower.includes(term)));
}

function searchError(error) {
  if (error?.response?.status === 202) return "Preparing the knowledge index. Try the search again in a moment.";
  if (error?.response?.status === 403) return "Your workspace does not have access to document search. You can still open an investigation.";
  return error?.response?.data?.detail || "Search is unavailable right now. Please try again.";
}

export default function TechnicianAssist() {
  const navigate = useNavigate();
  const recognition = useRef(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("");
  const voiceSupported = Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => () => recognition.current?.stop(), []);

  function openInvestigation(text) {
    setMode("investigation");
    navigate(`/secure/my-incidents?new=1&title=${encodeURIComponent(text.slice(0, 180))}`);
  }

  async function search(text) {
    setMode("search");
    setError("");
    setResults(null);
    const demoAlarm = demoAlarmFor(text);
    if (demoAlarm) {
      setResults([{ id: `haas-${demoAlarm.code}`, demoAlarm }]);
      return;
    }
    setLoading(true);
    try {
      const response = await api.post("/api/secure/search", { query: text });
      if (response.status === 202) {
        setError("Preparing the knowledge index. Try the search again in a moment.");
        return;
      }
      setResults(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(searchError(err));
    } finally {
      setLoading(false);
    }
  }

  function submit(event) {
    event.preventDefault();
    const text = query.trim();
    if (!text || loading) return;
    setError("");
    if (INVESTIGATION_REQUEST.test(text)) openInvestigation(text);
    else search(text);
  }

  function toggleVoice() {
    if (listening) { recognition.current?.stop(); return; }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const instance = new SpeechRecognition();
    recognition.current = instance;
    instance.lang = navigator.language || "en-US";
    instance.interimResults = false;
    instance.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript?.trim();
      if (transcript) setQuery((current) => [current.trim(), transcript].filter(Boolean).join(" "));
    };
    instance.onerror = (event) => setError(event.error === "not-allowed" ? "Microphone access was denied." : "Voice capture stopped. You can type your question instead.");
    instance.onend = () => { setListening(false); recognition.current = null; };
    try { instance.start(); setListening(true); }
    catch { setError("Voice capture could not start. You can type your question instead."); }
  }

  return <main className="mx-auto max-w-5xl px-5 py-10 text-slate-900 dark:text-white">
    <div className="mb-8">
      <p className="mb-2 text-xs font-bold uppercase tracking-widest text-primary">Fluxonix Ops</p>
      <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">What do you need to resolve?</h1>
      <p className="mt-3 max-w-2xl text-slate-600 dark:text-slate-300">Ask about an error, equipment, or procedure. If you ask to start an investigation, we’ll prepare an incident workspace for your review.</p>
    </div>
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <label htmlFor="assist-query" className="mb-2 block text-sm font-semibold">Describe the issue or ask a question</label>
      <textarea id="assist-query" rows="3" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="What does alarm 702 mean? Or: Start an investigation into repeated motor faults." className="w-full resize-y rounded-xl border border-slate-300 bg-white p-4 text-base text-slate-900 focus:border-primary focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-white" />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="submit" disabled={!query.trim() || loading} className="rounded-lg bg-primary px-5 py-3 font-semibold text-white disabled:opacity-50">{loading ? "Searching…" : "Continue"}</button>
        <button type="button" onClick={toggleVoice} disabled={!voiceSupported} aria-pressed={listening} className="rounded-lg border border-slate-300 px-4 py-3 font-medium disabled:opacity-50 dark:border-slate-600">{listening ? "Stop recording" : "Use voice"}</button>
        {!voiceSupported && <span className="text-sm text-slate-500">Voice is unavailable in this browser.</span>}
      </div>
    </form>
    {error && <p role="alert" className="mt-6 rounded-xl bg-amber-50 p-4 text-amber-900">{error}</p>}
    {mode === "search" && results && <section className="mt-8" aria-live="polite">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><h2 className="text-xl font-bold">Knowledge results</h2><button type="button" onClick={() => openInvestigation(query.trim())} className="text-sm font-semibold text-primary underline">Start an investigation instead</button></div>
      {results.length === 0 ? <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900"><p>No matching knowledge was found. Try a more specific error code or machine name.</p><button type="button" onClick={() => openInvestigation(query.trim())} className="mt-4 font-semibold text-primary underline">Open an investigation</button></div> : <div className="space-y-3">{results.map((item, index) => <article key={item.id || index} className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">{item.demoAlarm ? <><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Public example · Haas Servo Bar 300</div><h3 className="mt-2 text-xl font-bold">Alarm {item.demoAlarm.code}: {item.demoAlarm.title}</h3><p className="mt-3 text-sm text-slate-600 dark:text-slate-300">The OEM guide lists these possible causes and checks. Confirm the exact machine and observed condition before acting.</p><ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-200">{item.demoAlarm.checks.map((check) => <li key={check}>{check}</li>)}</ul><a href={HAAS_GUIDE} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block text-sm font-semibold text-primary underline">Open the Haas guide for diagrams and full procedure</a><p className="mt-2 text-xs text-slate-500">Illustrative public documentation; this is not your workspace's service content.</p></> : <><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.document_type || item.source_type || "Document"}</div><p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-200">{item.raw_text}</p>{item.file_url && /^(https?:\/\/|\/)/i.test(item.file_url) && <a href={item.file_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm font-semibold text-primary underline">Open source document</a>}</>}</article>)}</div>}
      <p className="mt-4 text-sm text-slate-500">Review the original source and machine configuration before performing service work.</p>
    </section>}
  </main>;
}
